import type { Movie, Puzzle } from '../types';
import { EPOCH_UTC } from './constants';

export function dayIndex(now: Date = new Date()): number {
  const utcMidnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.floor((utcMidnight - EPOCH_UTC) / 86400000);
}

// Deterministic mapping from day index to (movie, word). Movie index walks the
// catalog modulo its length; word index advances once per full catalog cycle,
// so the same (movie, word) combo recurs only after `catalog.length × pool.length`
// days — roughly four months for the current 24×6 layout.
export function pickPuzzle(catalog: readonly Movie[], day: number): Puzzle {
  if (catalog.length === 0) {
    throw new Error('Catalog is empty — nothing to play.');
  }
  const movieIdx = ((day % catalog.length) + catalog.length) % catalog.length;
  const movie = catalog[movieIdx]!;
  const wordIdx = Math.floor(Math.abs(day) / catalog.length) % movie.words.length;
  const word = movie.words[wordIdx]!;
  return { dayIndex: day, movie, word };
}

// Try the Pages Function for server-authoritative day index, fall back to the
// local clock. The answer itself still comes from the local catalog — the
// roadmap calls for moving that server-side once KV is wired up.
export async function loadPuzzle(catalog: readonly Movie[]): Promise<Puzzle> {
  const override = getOverridePuzzle(catalog);
  if (override) return override;

  let day = dayIndex();
  try {
    const res = await fetch('/api/daily', { cache: 'no-store' });
    if (res.ok) {
      const data = (await res.json()) as { dayIndex?: number };
      if (typeof data.dayIndex === 'number') day = data.dayIndex;
    }
  } catch {
    // offline / function not running — local clock is fine
  }
  return pickPuzzle(catalog, day);
}

// Test-only puzzle overrides. Gated to local dev so prod players can't peek
// at future puzzles. Supported query params:
//   ?day=N             — force a specific day index
//   ?movie=<id>        — force a movie by catalog id (first word by default)
//   ?movie=<id>&word=WORD       — force a specific word from that movie
//   ?movie=<id>&wordIdx=N       — force a word by index in the movie's pool
// Used by Playwright and pre-release smoke tests to iterate all 144 combos.
export function getOverridePuzzle(catalog: readonly Movie[]): Puzzle | null {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname;
  const allowed =
    import.meta.env.DEV || host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
  if (!allowed) return null;

  const params = new URLSearchParams(window.location.search);

  const dayParam = params.get('day');
  if (dayParam !== null) {
    const day = Number.parseInt(dayParam, 10);
    if (Number.isFinite(day)) return pickPuzzle(catalog, day);
  }

  const movieParam = params.get('movie');
  if (movieParam) {
    const movieIdx = catalog.findIndex((m) => m.id === movieParam);
    if (movieIdx < 0) return null;
    const movie = catalog[movieIdx]!;

    let wordIdx = 0;
    const wordParam = params.get('word');
    const wordIdxParam = params.get('wordIdx');
    if (wordParam) {
      const idx = movie.words.indexOf(wordParam.toUpperCase());
      if (idx >= 0) wordIdx = idx;
    } else if (wordIdxParam !== null) {
      const parsed = Number.parseInt(wordIdxParam, 10);
      if (Number.isFinite(parsed)) {
        wordIdx = ((parsed % movie.words.length) + movie.words.length) % movie.words.length;
      }
    }

    // Synthesize a stable day index that picks this exact combo, so persistence
    // still keys off something deterministic per (movie, word) pair.
    const day = wordIdx * catalog.length + movieIdx;
    return { dayIndex: day, movie, word: movie.words[wordIdx]! };
  }

  return null;
}
